import { RecurringRevenueCoverage } from '@interfaces/reports/recurringRevenue';

/**
 * How many subscriptions are active today - or the honest admission that the response did not say.
 *
 * ONE door for the question, because the alternative is what the last rounds of #267 kept finding: every
 * consumer deciding for itself what a zero means. The hook used to fill a missing `activeSubscribers` with 0,
 * which is indistinguishable from a real zero, and from there each screen guessed - one hid the whole
 * subscription layout, another printed "hoje · 0 assinantes ativos" beside a positive MRR. Same missing field,
 * three different wrong answers.
 *
 * `null` from the hook means the payload did not carry it. That is deliberately NOT zero: absent and empty are
 * different facts, and the screens have to be able to tell them apart to say either one.
 */
export type Population = { known: true; value: number } | { known: false };

const usable = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

/**
 * The snapshot answers first, the coverage second.
 *
 * `coverage.current.total` is the same population measured for a different purpose - the denominator the
 * coverage share is taken over, described by the API as the base active right now - so a payload that carries
 * the coverage but drops the snapshot count still knows the answer. Using it is not a guess; using zero was.
 */
export const resolvePopulation = (
  snapshot: { activeSubscribers: number | null },
  coverage: RecurringRevenueCoverage,
): Population => {
  if (usable(snapshot.activeSubscribers))
    return { known: true, value: snapshot.activeSubscribers };

  /**
   * An explicit ZERO counts here, and that is the whole reason `total` is nullable.
   *
   * A response that carried the coverage and reported an empty base HAS answered the question - discarding it
   * for being zero threw away the only population a partial payload supplied, and left the screens treating a
   * genuinely empty community as unmeasurable. Codex, #267.
   */
  const fromCoverage = coverage.current.total;

  if (usable(fromCoverage)) return { known: true, value: fromCoverage };

  return { known: false };
};

/**
 * The caption beside a money card, which must not state a count the response never sent.
 *
 * "hoje · 0 assinantes ativos" next to a positive MRR is a precise claim built from a missing field, and the
 * precision is what makes it worse than saying nothing: a producer has no way to read it as "unknown".
 */
export const describePopulation = (
  population: Population,
  noun = 'assinantes ativos',
) =>
  population.known ? `${population.value} ${noun}` : `${noun} não informados`;
